import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../../api/client';
import {
  getStudioRooms,
  reorderStudioRooms,
  updateRoomVisibility,
  type OwnerRoomSummary,
} from '../../api/partnerRooms';
import { useToast } from '../../components/ui/ToastProvider';

const ROOM_STATUS_LABELS: Record<string, string> = {
  LISTED: '노출 중',
  HIDDEN: '숨김',
  DRAFT: '작성 중',
};

function roomBadgeClass(status: string) {
  if (status === 'LISTED') return 'partner-badge partner-badge--green';
  if (status === 'HIDDEN') return 'partner-badge';
  return 'partner-badge partner-badge--yellow';
}

function formatWon(value: number | null | undefined) {
  return `${(value ?? 0).toLocaleString('ko-KR')}원`;
}

export function PartnerRoomsPage() {
  const { studioId } = useParams();
  const { showToast } = useToast();

  const [rooms, setRooms] = useState<OwnerRoomSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    if (!studioId) return;
    setErrorMessage('');
    try {
      const list = await getStudioRooms(studioId);
      setRooms(list);
    } catch (error) {
      setErrorMessage(
        error instanceof ApiError && error.message
          ? error.message
          : '룸 목록을 불러오지 못했습니다.',
      );
    }
  }, [studioId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const move = async (index: number, delta: -1 | 1) => {
    if (!studioId || acting) return;
    const target = index + delta;
    if (target < 0 || target >= rooms.length) return;
    const next = rooms.slice();
    [next[index], next[target]] = [next[target], next[index]];
    setActing(true);
    setRooms(next);
    try {
      const updated = await reorderStudioRooms(
        studioId,
        next.map((room) => room.roomId),
      );
      setRooms(updated);
    } catch (error) {
      showToast(
        error instanceof ApiError && error.message ? error.message : '순서 변경에 실패했습니다.',
      );
      await load();
    } finally {
      setActing(false);
    }
  };

  const toggleVisibility = async (room: OwnerRoomSummary) => {
    if (acting) return;
    const nextVisibility = room.status === 'LISTED' ? 'HIDDEN' : 'LISTED';
    setActing(true);
    try {
      await updateRoomVisibility(room.roomId, nextVisibility);
      showToast(nextVisibility === 'LISTED' ? '룸을 노출했습니다.' : '룸을 숨겼습니다.');
      await load();
    } catch (error) {
      showToast(
        error instanceof ApiError && error.message ? error.message : '처리에 실패했습니다.',
      );
    } finally {
      setActing(false);
    }
  };

  return (
    <>
      <h1 className="partner-title">룸 관리</h1>
      <p className="partner-subtitle">룸 노출 순서와 상태를 관리합니다.</p>

      <div className="partner-toolbar">
        <Link className="partner-btn partner-btn--ghost partner-btn--sm" to="/partner/studios">
          ← 스튜디오 목록
        </Link>
        <div className="partner-toolbar__spacer" />
        <Link className="partner-btn" to={`/partner/studios/${studioId}/rooms/new`}>
          + 룸 등록
        </Link>
      </div>

      {errorMessage ? <div className="partner-error">{errorMessage}</div> : null}

      <div className="partner-card">
        {loading ? (
          <p className="partner-empty">불러오는 중…</p>
        ) : rooms.length === 0 ? (
          <p className="partner-empty">등록된 룸이 없습니다. 첫 룸을 등록해보세요.</p>
        ) : (
          <div className="partner-table-wrap">
            <table className="partner-table">
              <thead>
                <tr>
                  <th>순서</th>
                  <th>룸</th>
                  <th>기본가</th>
                  <th>평점</th>
                  <th>상태</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {rooms.map((room, index) => (
                  <tr key={room.roomId}>
                    <td>
                      <span style={{ display: 'inline-flex', gap: 4 }}>
                        <button
                          aria-label="위로"
                          className="partner-btn partner-btn--ghost partner-btn--sm"
                          disabled={acting || index === 0}
                          onClick={() => move(index, -1)}
                          type="button"
                        >
                          ↑
                        </button>
                        <button
                          aria-label="아래로"
                          className="partner-btn partner-btn--ghost partner-btn--sm"
                          disabled={acting || index === rooms.length - 1}
                          onClick={() => move(index, 1)}
                          type="button"
                        >
                          ↓
                        </button>
                      </span>
                    </td>
                    <td>
                      <strong>{room.name}</strong>
                      {room.location ? (
                        <>
                          <br />
                          <span style={{ color: '#8a93a0', fontSize: 12.5 }}>{room.location}</span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      {formatWon(room.basePrice)}
                      {room.priceUnit ? `/${room.priceUnit}` : ''}
                    </td>
                    <td>
                      {room.rating ?? '—'} ({room.reviewCount})
                    </td>
                    <td>
                      <span className={roomBadgeClass(room.status)}>
                        {ROOM_STATUS_LABELS[room.status] ?? room.status}
                      </span>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <Link
                          className="partner-btn partner-btn--ghost partner-btn--sm"
                          to={`/partner/rooms/${room.roomId}`}
                        >
                          수정
                        </Link>
                        <button
                          className={`partner-btn partner-btn--sm${room.status === 'LISTED' ? ' partner-btn--danger' : ''}`}
                          disabled={acting}
                          onClick={() => toggleVisibility(room)}
                          type="button"
                        >
                          {room.status === 'LISTED' ? '숨기기' : '노출하기'}
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
